import { DataUndefined, EmeraldItemNotFound } from './../../common/helpers/error-code';
import { EmeraldError, EmeraldErrorModal } from 'src/common/api/emerald-error';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import { IDataInformation } from 'src/common/interfaces/dtos/IDataInformation';
import { MessageModalSizeEnum } from 'src/common/interfaces/dtos/IMessageModal';
import { IElectronicJournalRecord } from 'src/common/interfaces/states/IElectronicJournalRecord';
import { store } from 'src/common/redux/configure-store';
import { dataAdd } from 'src/home-page/business-logic/service/data-add';
import { ILineItemAddData } from 'src/items-list/business-logic/line-item-add';
import { performOnlineItemAuthorizationItemSale } from '../authorize-online-items';
import { OnlineItemAuthorizationTrigger } from '../enumerations/OnlineItemAuthorizationTrigger';
import { addOnlineItemByExternalId } from './add-online-item';

export const matchBarcodeToItemId = async (
  lineItemData: ILineItemAddData,
  barcode: string,
  authTrigger?: string,
): Promise<void> => {
  const transactionId = store.getState().transaction.transactionId;

  const decodedInBarcodelIdValues: DecodeInBarcodeResult = await dataAdd(transactionId, {
    itemId: barcode,
  })
    .then(result => {
      return {
        itemId: result.itemId ?? '',
        externalId: result.onlineItemExternalId ?? '',
      };
    })
    .catch(() => {
      throw new EmeraldError('CannotDecodeInBarcodeItemId');
    });

  return new Promise<void>((resolve, reject) => {
    const itemId = lineItemData.lineItem.item.id;
    const decodedItemId = decodedInBarcodelIdValues.itemId;

    if (itemId !== decodedItemId) {
      reject(barcodeMismatchModal);
    } else {
      if (authTrigger === OnlineItemAuthorizationTrigger.ItemSale) {
        return performOnlineItemAuthorizationItemSale(
          lineItemData,
          decodedInBarcodelIdValues.externalId ?? '',
        )
          .then(resolve)
          .catch(reject);
      } else {
        return addOnlineItemByExternalId(lineItemData, decodedInBarcodelIdValues.externalId ?? '')
          .then(resolve)
          .catch(reject);
      }
    }
  });
};

export const compareBarcodeToLine = async (
  data: string,
  onlineItems: IElectronicJournalRecord[],
): Promise<IElectronicJournalRecord> => {
  const transactionId = store.getState().transaction.transactionId;
  const decodedData = await dataAdd(transactionId, {
    itemId: data,
  }).catch((err): IDataInformation | never => {
    if (err.code === DataUndefined) {
      return {
        onlineItemExternalId: data,
      };
    } else {
      throw err;
    }
  });
  if (decodedData.onlineItemExternalId === undefined) {
    const scannedData = decodedData.scanData;
    decodedData.onlineItemExternalId = scannedData;
  }
  const itemToVoid = onlineItems.find(item => compare(decodedData, item));
  if (itemToVoid) {
    return itemToVoid;
  } else {
    throw new EmeraldError(EmeraldItemNotFound);
  }
};

const compare = (decodedData: IDataInformation, item: IElectronicJournalRecord): boolean => {
  if (item.OnlineItem) {
    if (item.OnlineItem.authorizationExternalId === decodedData.onlineItemExternalId) {
      return true;
    }
  }
  return false;
};

export interface DecodeInBarcodeResult {
  itemId?: string;
  externalId?: string;
}

const barcodeMismatchModal = new EmeraldErrorModal({
  message: 'BarcodeMismatch',
  subMessage: 'BarcodeDoesntMatchItem',
  type: EmeraldDialogTypes.Error,
  size: MessageModalSizeEnum.Medium,
  dialogTestIdPrefix: 'online-item-barcode-mismatch',
  acceptButtonLabel: 'Ok',
  hideCancelButton: true,
  disableBackdropClick: true,
  dialogHeight: 54.427,
});
